import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import ScrollToTopOnMount from '../../components/Effects/ScrollToTopOnMount'
import moment from '../../utilities/Moment'
import NumberFormatter from '../../utilities/NumberFormatter'
import { ExpensesController } from '../../controllers/accountant'
import { AccountantApiError } from '../../controllers/accountant/request'
import { AccountantWorkspace, Expense } from '../../types'
import ExportExpensesModal from '../../components/Modals/Accountant/ExportExpensesModal'
import { cn } from '../../lib/utils'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip'
import { DownloadIcon, EllipsisIcon, FileTextIcon, PaperclipIcon } from 'lucide-react'

interface IProps {
  workspace: AccountantWorkspace
  onNotFound: () => void
}

const ExpenseStatusBadge = ({ expense }: { expense: Expense }) => {
  const { t } = useTranslation()

  if (expense.paid_on) {
    return <Badge variant="secondary">{t('ExpenseHelper::Paid')}</Badge>
  } else if (expense.due_on && moment(expense.due_on).isSameOrBefore(moment(), 'day')) {
    return <Badge variant="destructive">{t('ExpenseHelper::Overdue')}</Badge>
  } else if (expense.due_on) {
    return <Badge variant="outline">{t('ExpenseHelper::Outstanding')}</Badge>
  }

  return <>{'-'}</>
}

export default function Expenses(props: IProps) {
  const { workspace: { setting } } = props
  const { t } = useTranslation()

  const [expenses, setExpenses] = React.useState<Expense[]>([])
  const [currentPage, setCurrentPage] = React.useState(1)
  const [perPage] = React.useState(25)
  const [totalPages, setTotalPages] = React.useState(1)
  const [didInitialLoad, setDidInitialLoad] = React.useState(false)
  const [showExportExpensesModal, setShowExportExpensesModal] = React.useState(false)

  // Ignore responses that arrive after the workspace was switched
  const activeWorkspaceId = React.useRef(props.workspace.id)
  activeWorkspaceId.current = props.workspace.id

  const fetchExpenses = (page = 1) => {
    const requestedWorkspaceId = props.workspace.id

    ExpensesController
      .getExpenses(props.workspace.id, { page: page, per_page: perPage })
      .then(response => {
        if (activeWorkspaceId.current !== requestedWorkspaceId) return

        const { expenses: newExpenses, current_page, total_pages } = response

        setExpenses(newExpenses)
        setCurrentPage(current_page)
        setTotalPages(total_pages)
        setDidInitialLoad(true)
      })
      .catch(error => {
        if (activeWorkspaceId.current !== requestedWorkspaceId) return
        if (error instanceof AccountantApiError && error.status === 404) props.onNotFound()
        else console.error(error)
      })
  }

  React.useEffect(() => {
    setDidInitialLoad(false)
    fetchExpenses(1)
  }, [props.workspace.id])

  const onDownloadClick = (expense: Expense) => {
    window.location.assign(ExpensesController.downloadUrl(props.workspace.id, expense.id))
  }

  return (
    <>
      <Helmet>
        <title>{'Bizzey | Uitgaven'}</title>
      </Helmet>
      <ScrollToTopOnMount />

      <div className="flex items-center justify-end mb-4">
        <Button onClick={() => setShowExportExpensesModal(true)}>
          <DownloadIcon />
          {'Export expenses'}
        </Button>
      </div>

      {!didInitialLoad && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      )}

      {didInitialLoad && expenses.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border py-24 text-center">
          <FileTextIcon className="size-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{'No expenses found'}</p>
        </div>
      )}

      {didInitialLoad && expenses.length > 0 && (
        <TooltipProvider>
          <div className="rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Expense::Details')}</TableHead>
                  <TableHead>{t('Expense::supplier')}</TableHead>
                  <TableHead>{t('Expense::invoiced_on')}</TableHead>
                  <TableHead>{t('Expense::status')}</TableHead>
                  <TableHead>{t('Expense::attachment')}</TableHead>
                  <TableHead className="text-right">{t('Expense::amount')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell className="max-w-75">
                      {expense.category && (
                        <div className="truncate text-sm font-medium" style={{ color: expense.category.color }}>
                          {expense.category.name}
                        </div>
                      )}
                      <div className="truncate" title={expense.name}>{expense.name || '-'}</div>
                    </TableCell>
                    <TableCell className="max-w-75 truncate">
                      {expense.supplier?.name || '-'}
                    </TableCell>
                    <TableCell>
                      {expense.invoiced_on ? moment(expense.invoiced_on).format(setting.date_format) : '-'}
                    </TableCell>
                    <TableCell>
                      <ExpenseStatusBadge expense={expense} />
                    </TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger render={<span className="inline-flex" />}>
                          <PaperclipIcon className={cn('size-4', expense.attachment_url ? 'text-foreground' : 'text-muted-foreground/40')} />
                        </TooltipTrigger>
                        <TooltipContent>
                          {expense.attachment_url ? t('Expense::Receipt attached') : t('Expense::Missing receipt')}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {NumberFormatter.formatCurrency(expense.currency || setting.default_currency, setting.number_format, expense.amount)}
                    </TableCell>
                    <TableCell>
                      {expense.attachment_url && (
                        <DropdownMenu>
                          <DropdownMenuTrigger render={<Button variant="ghost" size="icon-sm" />}>
                            <EllipsisIcon />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuGroup>
                              <DropdownMenuLabel>{'Actions'}</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => onDownloadClick(expense)}>
                                <FileTextIcon />
                                {'Download'}
                              </DropdownMenuItem>
                            </DropdownMenuGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TooltipProvider>
      )}

      {didInitialLoad && totalPages > 1 && (
        <Pagination className="mt-4 justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  currentPage > 1 && fetchExpenses(currentPage - 1)
                }}
              />
            </PaginationItem>
            {Array.from({ length: totalPages }).map((_, index) => (
              <PaginationItem key={index}>
                <PaginationLink
                  href="#"
                  isActive={currentPage === index + 1}
                  onClick={(e) => {
                    e.preventDefault()
                    fetchExpenses(index + 1)
                  }}
                >
                  {index + 1}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  currentPage < totalPages && fetchExpenses(currentPage + 1)
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <ExportExpensesModal
        workspaceId={props.workspace.id}
        open={showExportExpensesModal}
        onOpenChange={setShowExportExpensesModal}
      />
    </>
  )
}
